import {
  Badge,
  Button,
  HStack,
  Header,
  Image,
  InlineAlert,
  Pressable,
  VStack,
  addWithConfig,
} from '@rbilabs/universal-components';
import { StyleSheet, View } from 'react-native';

import Picture from 'components/picture';
import { useConfigOfferDateRangeMessage } from 'hooks/loyalty/use-config-offer-date-range-message';
import { useOfferExpiration } from 'hooks/loyalty/use-offer-expiration';
import { OfferFeedbackMessage } from 'pages/cart/cart-item/styled';
import { useLoyaltyWalmart } from 'pages/loyalty/loyalty-partners/walmart/use-loyalty-walmart';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { getWalmartSeasonalConfigOfferIds } from 'state/loyalty/hooks/utils/walmart';
import { primitive } from 'styles/constants/primitives';

import { WalmartBadgeContainer } from './styled';
import type { OfferListItemProps } from './types';

const _OfferListItem = ({
  description,
  serviceModeStyle,
  serviceModeText,
  handleRemove,
  header,
  id,
  image,
  imageAlt,
  imageUri,
  offerApplied,
  offerText,
  onPress,
  promoCodeLabel,
  removeOfferText,
  unavailable,
  unavailableMessage,
  unavailableIcon,
  isWalmartOffer,
  isWalmartUser,
  isWalmartPaidMember,
  endDate,
  expirationDate,
  engineId,
  ...buttonProps
}: OfferListItemProps) => {
  const { data: loyaltyWalmartData } = useLoyaltyWalmart();

  const { offersCooldown } = useLoyaltyContext();
  const isWalmartSeasonalOffer =
    id && getWalmartSeasonalConfigOfferIds(loyaltyWalmartData).includes(id);

  const expiresAt = useOfferExpiration(
    endDate,
    expirationDate,
    !!(isWalmartOffer || isWalmartSeasonalOffer)
  );
  const configOfferDateRange = useConfigOfferDateRangeMessage(
    (isWalmartSeasonalOffer &&
      loyaltyWalmartData?.seasonalPromoConfig?.configOffers?.find(
        configOffer => configOffer?._id === id
      )) ||
      undefined
  );

  // Hide any Walmart offers if not a Walmart user
  if (!offerApplied && (isWalmartOffer || isWalmartSeasonalOffer) && !isWalmartUser) {
    return null;
  }

  const showWalmartBadge = isWalmartOffer || isWalmartSeasonalOffer;

  return (
    <Pressable
      accessibilityRole="button"
      dd-action-name={header}
      onPress={!offersCooldown.isActive ? onPress : null}
      mb="$2.5"
      width="full"
      {...buttonProps}
      testID="offer-list-item"
    >
      {({ isHovered, isPressed, isFocused }) => {
        const pressed = isPressed && onPress;
        const offerIsApplied = offerApplied && offerText && handleRemove && removeOfferText;
        return (
          <View
            dataSet={{
              sanityId: id,
              engineId,
              availability: unavailable ? 'unavailable' : 'available',
            }}
            style={[
              styles.offerListItemBox,
              ((unavailable && unavailableMessage) || offersCooldown.isActive) &&
                styles.offerListItemBoxUnavailable,
              offerApplied && styles.offerListItemBoxApplied,
            ]}
          >
            <VStack
              p={['$3', '$4']}
              pt={offerIsApplied ? '$1' : '$3'}
              borderTopRadius={10}
              borderBottomRadius={unavailable ? 0 : 10}
              bg={pressed || isFocused ? 'token.background-focus' : 'transparent'}
            >
              {offerIsApplied && (
                <HStack justifyContent="space-between" alignItems="center" pl="$2" mb="$2">
                  {!!offerText && (
                    <Badge
                      variant="default-subtle"
                      iconName="checkFill"
                      backgroundColor={primitive.bk.$successLight}
                    >
                      {offerText}
                    </Badge>
                  )}
                  <Button variant="ghost" onPress={handleRemove} size="md">
                    {removeOfferText}
                  </Button>
                </HStack>
              )}
              <HStack mr="$2">
                {image || (
                  <Image
                    accessibilityIgnoresInvertColors
                    resizeMode="contain"
                    source={{
                      uri: imageUri,
                    }}
                    alt={imageAlt}
                    height={['$24', '112']}
                    width={['$24', '112']}
                  />
                )}
                <VStack
                  flexShrink={1}
                  ml="$3"
                  alignItems="flex-start"
                  justifyContent="center"
                  space={2}
                >
                  <Header
                    variant="headerThree"
                    color={isHovered ? 'token.text-active' : 'token.text-default'}
                    ellipsizeMode="tail"
                    numberOfLines={2}
                    accessibilityLevel={2}
                    marginBottom={1}
                  >
                    {header}
                  </Header>
                  {description}
                  {expiresAt && <OfferFeedbackMessage>{expiresAt}</OfferFeedbackMessage>}
                  {configOfferDateRange && (
                    <OfferFeedbackMessage>{configOfferDateRange}</OfferFeedbackMessage>
                  )}
                  <HStack>
                    {showWalmartBadge && (
                      <WalmartBadgeContainer mr="$2">
                        <Picture
                          height="$6"
                          image={loyaltyWalmartData?.badge?.locale}
                          alt={t('walmartOfferBadgeAlt')}
                        />
                      </WalmartBadgeContainer>
                    )}
                    {promoCodeLabel && (
                      <Badge variant="default-outline" iconName="offer" mr="$2">
                        {promoCodeLabel}
                      </Badge>
                    )}
                    {serviceModeText && (
                      <Badge variant={serviceModeStyle ?? 'default-solid'}>{serviceModeText}</Badge>
                    )}
                  </HStack>
                </VStack>
              </HStack>
            </VStack>
            {unavailable && !offerApplied && (
              <InlineAlert
                borderBottomRadius={10}
                borderTopRadius="0"
                width="full"
                status={unavailableIcon || 'info'}
                message={unavailableMessage}
                w="full"
              />
            )}
          </View>
        );
      }}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  offerListItemBox: {
    backgroundColor: primitive.$white,
    borderRadius: 10,
    borderWidth: 0,
    borderColor: 'transparent',
  },
  offerListItemBoxUnavailable: {
    backgroundColor: primitive.bk.$disabledNoOpacity,
  },
  offerListItemBoxApplied: {
    borderWidth: 2,
    borderColor: primitive.bk.$bbqBrown,
  },
});

export const OfferListItem = addWithConfig(_OfferListItem);
